// nono-machine.js: 00 Felles §09 «Maskinkort» (sections/p-machine-dialog.liquid, owner yes 19.10.2026).
// «Se maskinen» ([data-machine-open="0N"]) opens the shared modal <dialog id="nono-maskin"> with that machine's
// <template> cloned in. Nothing is fetched before the first open: the CSS (assets/nono-machine.css) is requested on
// the first intent (hover / focus / touch) and awaited on open; the photos sit in the templates until cloned.
//   Gallery: arrows, thumbnails (PC) / dots (phone), swipe (native scroll-snap), ← → keys, «1 / 4» aria-live.
//   Round: «Forrige maskin» / «Neste maskin» 01 → 08, wrapping.
//   Dialog: focus to the title on open, native focus trap (showModal), Esc closes, focus back to the trigger,
//   iOS-safe page scroll lock, reduced motion = no smooth scroll and no fade.
// Without JS «Se maskinen» is a plain link to /pages/slik-fungerer-nono#maskin-0N (the card on that page).

const d = document;
const dialog = d.getElementById('nono-maskin');
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

if (dialog && dialog.showModal) {
  const inner = dialog.querySelector('[data-md-inner]');
  const tpl = (num) => d.querySelector(`template[data-machine-tpl="${num}"]`);
  const order = [...d.querySelectorAll('template[data-machine-tpl]')].map((t) => t.dataset.machineTpl);
  let opener = null;
  let current = null;
  let lockY = null;
  let css = null;

  const loadCss = () => {
    if (css) return css;
    const href = dialog.dataset.css;
    const have = href && d.querySelector(`link[href="${href}"]`);
    if (!href || have) return (css = Promise.resolve());
    const link = Object.assign(d.createElement('link'), { rel: 'stylesheet', href });
    css = new Promise((resolve) => {
      link.onload = resolve;
      link.onerror = resolve;
      setTimeout(resolve, 2500);
    });
    d.head.append(link);
    return css;
  };

  const lock = () => {
    if (lockY !== null) return;
    lockY = scrollY;
    Object.assign(d.body.style, { position: 'fixed', top: `-${lockY}px`, left: '0', right: '0' });
  };
  const unlock = () => {
    if (lockY === null) return;
    Object.assign(d.body.style, { position: '', top: '', left: '', right: '' });
    scrollTo({ top: lockY, behavior: 'instant' });
    lockY = null;
  };

  // ---- gallery -------------------------------------------------------------------------------------------------
  const gallery = (root) => {
    const track = root.querySelector('[data-md-track]');
    if (!track) return;
    const slides = [...track.children];
    const n = slides.length;
    const count = root.querySelector('[data-md-count]');
    const thumbs = [...root.querySelectorAll('[data-md-thumb]')];
    let at = 0;
    const mark = (i) => {
      if (i === at && count.dataset.ready) return;
      at = i;
      count.dataset.ready = '1';
      count.textContent = `${i + 1} / ${n}`;
      slides.forEach((s, k) => s.toggleAttribute('aria-hidden', k !== i));
      thumbs.forEach((t, k) => (k === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
    };
    const go = (i) => {
      const k = (i + n) % n;
      track.scrollTo({ left: slides[k].offsetLeft, behavior: reduced() ? 'instant' : 'smooth' });
      mark(k);
    };
    let queued = 0;
    track.addEventListener('scroll', () => {
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        const w = track.clientWidth || 1;
        mark(Math.min(n - 1, Math.max(0, Math.round(track.scrollLeft / w))));
      });
    }, { passive: true });
    root.querySelector('[data-md-prev]')?.addEventListener('click', () => go(at - 1));
    root.querySelector('[data-md-next]')?.addEventListener('click', () => go(at + 1));
    thumbs.forEach((t, k) => t.addEventListener('click', () => go(k)));
    root.addEventListener('keydown', (e) => {
      if (e.target.closest('a, input, textarea')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); go(at + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(at - 1); }
    });
    // Photo 1 is the first thing seen: fetch it now; the rest stay lazy (they are off-screen in the track).
    const first = slides[0]?.querySelector('img');
    if (first) first.loading = 'eager';
    mark(0);
  };

  // ---- render one machine into the shell -----------------------------------------------------------------------
  const render = (num) => {
    const t = tpl(num);
    if (!t) return false;
    current = num;
    inner.replaceChildren(t.content.cloneNode(true));
    gallery(inner);
    return true;
  };

  const open = async (num, trigger) => {
    if (!tpl(num)) return false;
    opener = trigger || opener;
    await loadCss();
    render(num);
    if (!dialog.open) {
      lock();
      dialog.showModal();
      window.nonoTrack?.('machine_card_open', { machine: num }); // GA4
    }
    inner.querySelector('#nono-maskin-title')?.focus({ preventScroll: true });
    return true;
  };

  // Close now (not on the async «close» event), so a booking window opened next measures the right scroll.
  const close = (returnFocus = true) => {
    if (dialog.open) dialog.close();
    unlock();
    if (returnFocus && opener && opener.isConnected) opener.focus({ preventScroll: true });
  };

  // ---- events --------------------------------------------------------------------------------------------------
  const triggerOf = (e) => (e.target instanceof Element ? e.target.closest('[data-machine-open]') : null);
  const intent = (e) => { if (triggerOf(e)) loadCss(); };
  d.addEventListener('pointerover', intent, { passive: true });
  d.addEventListener('focusin', intent);
  d.addEventListener('touchstart', intent, { passive: true });

  d.addEventListener('click', (e) => {
    const t = triggerOf(e);
    if (!t || e.button || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    if (!tpl(t.dataset.machineOpen)) return; // no data for it: keep the link
    e.preventDefault();
    open(t.dataset.machineOpen, t);
  });

  dialog.addEventListener('click', (e) => {
    const el = e.target instanceof Element ? e.target : null;
    if (!el) return;
    if (el === dialog) { close(); return; } // the backdrop
    if (el.closest('[data-md-close]')) { close(); return; }
    const go = el.closest('[data-md-go]');
    if (go) {
      e.preventDefault();
      render(go.dataset.mdGo);
      inner.querySelector('[data-md-body]')?.scrollTo({ top: 0 });
      inner.querySelector('#nono-maskin-title')?.focus({ preventScroll: true });
      return;
    }
    // «Prøv Nono gratis»: hand over to the booking window (x-booking-bootstrap listens on document). Close this one
    // first; when the booking window closes, focus comes back to the «Se maskinen» that started it all.
    if (el.closest('[data-booking-trigger]')) {
      const back = opener;
      close(false);
      const booking = d.getElementById('nono-booking');
      if (booking && back) {
        booking.addEventListener('close', () => setTimeout(() => back.isConnected && back.focus({ preventScroll: true })), { once: true });
      }
    }
  });

  dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); }); // Esc
  dialog.addEventListener('close', () => { unlock(); });

  // Arrived with #maskin-0N on a page that has the dialog (shared link): open it.
  const m = /^#maskin-(0[1-8])$/.exec(location.hash);
  if (m && order.includes(m[1])) open(m[1], d.getElementById(`maskin-${m[1]}`)?.querySelector('[data-machine-open]'));
}
